<template>
  <div class="subscription">
    <SettingsHeader :title="$t('settings.subscription.heading')" />
    <div class="subscription__content">
      <div class="subscription__details">
        <h1 class="subscription__details__header">
          {{ $t('settings.subscription.details.header') }}
        </h1>
        <p class="subscription__details__text">
          {{ subscriptionDetailsText }}
        </p>
      </div>
      <div class="subscription__update-section">
        <div class="subscription__update-section__card-number">
          {{ cardBrand }} {{ cardNumber }}
        </div>
        <Button
          class="subscription__update-section__button"
          @click="updateSubscription"
          :loading="updating"
          :disabled="cancelSubscriptionAtPeriodEnd"
        >
          {{ $t('settings.subscription.buttons.editCard') }}
        </Button>
      </div>
      <div class="subscription__cancel-section">
        <Button
          @click="confirmSubscriptionCancel"
          :disabled="cancelSubscriptionAtPeriodEnd"
          type="danger"
        >
          Cancel subscription
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import subscriptionUpdateMixin from '@/common/mixins/subscription-update';
import SettingsHeader from './SettingsHeader';
import { Button } from '@/components/common';
import { maskCardNumber } from '@/common/helpers';

const { mapState, mapActions } = createNamespacedHelpers('user');

export default {
  name: 'Subscription',
  components: { Button, SettingsHeader },
  mixins: [subscriptionUpdateMixin],
  data() {
    return {
      canceling: false,
      updating: false,
    };
  },
  computed: {
    ...mapState({
      cardNumber: state => maskCardNumber(state.details.card.last4),
      cardBrand: state => state.details.card.brand,
      cancelSubscriptionAtPeriodEnd: state => state.details.cancelSubscriptionAtPeriodEnd,
      subscriptionDetailsText(state) {
        const { details: { cancelSubscriptionAtPeriodEnd, billingPeriodEnd } } = state;
        const date = this.$moment(billingPeriodEnd).format(this.$t('dateFormat.short'));
        return cancelSubscriptionAtPeriodEnd
          ? this.$t('settings.subscription.details.text-cancel', { date })
          : this.$t('settings.subscription.details.text-renew', { date });
      },
    }),
  },
  methods: {
    ...mapActions(['deleteSubscription']),
    confirmSubscriptionCancel() {
      this.$modals.confirm({
        header: this.$t('settings.subscription.modal.header'),
        content: this.$t('settings.subscription.modal.content'),
        confirmText: this.$t('settings.subscription.modal.buttons.confirm'),
        cancelText: this.$t('settings.subscription.modal.buttons.cancel'),
        onConfirm: this.cancelSubscription,
      });
    },
    async cancelSubscription() {
      try {
        this.canceling = true;
        await this.deleteSubscription();
        this.$notifications.success(this.$t('settings.subscription.cancelMsg'));
      } catch (error) {
        this.$notifications.error(error.message);
      } finally {
        this.canceling = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .subscription {
    width: 100%;

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      width: 100%;

      @include md-up {
        padding: 60px;
      }
    }

    &__details {
      margin-bottom: 50px;
      width: 100%;

      &__header {
        color: $primary;
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 20px;
        text-align: left;
      }

      &__text {
        color: $gray;
        font-size: 14px;
        font-weight: 500;
      }
    }

    &__update-section {
      margin-bottom: 80px;
      width: 100%;
      display: flex;

      &__card-number {
        padding-left: 24px;
        background-color: #f1f1f5;
        border: 1px solid #e5e7ef;
        border-radius: 10px;
        width: 260px;
        height: 50px;
        display: flex;
        align-items: center;
        font-size: 15px;
        font-weight: 500;
        text-transform: capitalize;
        margin-right: 15px;
      }

      &__button {
        // @todo extract this button to separate component
        background-color: $accent;
        color: $white;

        &:hover {
          background-color: darken($accent, 10%);
          transition: all .6s;
        }
      }
    }

    &__cancel-section {
      width: 100%;
    }
  }
</style>
